import { useEffect, useRef, useState } from "react";
import { ShieldCheck, ShieldAlert, ShieldX, UploadCloud, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import MobileScreenHeader from "../components/MobileScreenHeader";

const DOC_TYPES = [
    { value: "aadhaar",  label: "Aadhaar" },
    { value: "pan",      label: "PAN Card" },
    { value: "passport", label: "Passport" },
    { value: "driving_license", label: "Driving License" },
    { value: "voter_id", label: "Voter ID" },
    { value: "rera",     label: "RERA Certificate" },
    { value: "bar_council", label: "Bar Council ID" },
    { value: "other",    label: "Other" },
];

const STATUS_META = {
    approved:   { icon: ShieldCheck, color: "text-emerald-600", bg: "bg-emerald-50", label: "Approved", desc: "Your identity is verified" },
    pending:    { icon: ShieldAlert, color: "text-amber-600",   bg: "bg-amber-50",   label: "Pending Review", desc: "Admin will review shortly" },
    rejected:   { icon: ShieldX,     color: "text-red-600",     bg: "bg-red-50",     label: "Rejected", desc: "Please re-submit correct docs" },
    not_submitted: { icon: ShieldAlert, color: "text-[#8B6712]", bg: "bg-[#FBF6E0]", label: "Not Submitted", desc: "Upload a document to verify" },
};

export default function Kyc() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [docType, setDocType] = useState("aadhaar");
    const [docNumber, setDocNumber] = useState("");
    const [uploaded, setUploaded] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileInput = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/kyc/me");
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/kyc/upload-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setUploaded({ url: data.url, path: data.path, name: file.name });
            toast.success("Document uploaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!uploaded) return toast.error("Upload a document first");
        setSubmitting(true);
        try {
            await api.post("/kyc/submit", {
                document_type: docType,
                document_url: uploaded.url,
                doc_number: docNumber || null,
                notes: null,
            });
            toast.success("KYC submitted for review");
            setUploaded(null);
            setDocNumber("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const status = data?.status || "not_submitted";
    const meta = STATUS_META[status] || STATUS_META.not_submitted;
    const Icon = meta.icon;
    const canSubmit = status !== "approved" && status !== "pending";

    return (
        <div className="min-h-screen bg-[#f6f7fb]" data-testid="kyc-page">
            <MobileScreenHeader title="KYC Verification" subtitle="Verify identity & documents" testid="kyc-header" />

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#002657]/60">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <>
                    {/* Status card */}
                    <div className="px-4 -mt-2">
                        <div className="rounded-2xl bg-white p-4 shadow-md border border-[#002657]/10" data-testid={`kyc-status-${status}`}>
                            <div className="flex items-center gap-3">
                                <div className={`w-12 h-12 rounded-full ${meta.bg} flex items-center justify-center`}>
                                    <Icon className={`w-6 h-6 ${meta.color}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading font-extrabold text-[15px] text-[#002657]">{meta.label}</div>
                                    <div className="text-[11px] text-[#002657]/60">{meta.desc}</div>
                                </div>
                                {data?.verified && (
                                    <div className="text-[9px] font-extrabold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                                        <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> Verified
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Submission form */}
                    {canSubmit && (
                        <div className="mt-4 px-4">
                            <div className="rounded-2xl bg-white p-4 border border-[#002657]/10 shadow-sm space-y-3" data-testid="kyc-submit-form">
                                <div>
                                    <label className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712]">Document Type</label>
                                    <select
                                        value={docType}
                                        onChange={(e) => setDocType(e.target.value)}
                                        data-testid="kyc-doc-type"
                                        className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#f6f7fb] border border-[#002657]/15 text-[13px] font-semibold text-[#002657] focus:outline-none focus:border-[#DAA520]"
                                    >
                                        {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712]">Document Number (optional)</label>
                                    <input
                                        type="text"
                                        value={docNumber}
                                        onChange={(e) => setDocNumber(e.target.value)}
                                        placeholder="e.g. XXXX-XXXX-1234"
                                        data-testid="kyc-doc-number"
                                        className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#f6f7fb] border border-[#002657]/15 text-[13px] text-[#002657] focus:outline-none focus:border-[#DAA520]"
                                    />
                                </div>

                                {/* Uploader */}
                                <div>
                                    <label className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712]">Upload Document</label>
                                    <button
                                        type="button"
                                        onClick={() => fileInput.current?.click()}
                                        data-testid="kyc-upload-btn"
                                        disabled={uploading}
                                        className="mt-1 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-[#DAA520]/40 bg-[#FBF6E0]/30 text-[#8B6712] font-bold text-[13px] active:scale-[0.99] transition-transform disabled:opacity-60"
                                    >
                                        {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                                                    : <><UploadCloud className="w-4 h-4" /> {uploaded ? "Replace file" : "Choose file (JPG/PNG/PDF)"}</>}
                                    </button>
                                    <input ref={fileInput} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} />
                                    {uploaded && (
                                        <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 flex items-center gap-1.5" data-testid="kyc-uploaded-file">
                                            <FileText className="w-3.5 h-3.5" /> {uploaded.name}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={!uploaded || submitting}
                                    data-testid="kyc-submit-btn"
                                    className="w-full py-3 rounded-xl font-extrabold text-[14px] shadow disabled:opacity-50"
                                    style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                                >
                                    {submitting ? "Submitting…" : "Submit for Verification"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* History */}
                    {(data?.documents || []).length > 0 && (
                        <div className="mt-5 px-4 pb-6">
                            <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712] mb-2">Submission History</div>
                            <div className="space-y-2" data-testid="kyc-history">
                                {data.documents.map((d) => (
                                    <div key={d.id} className="rounded-2xl bg-white border border-[#002657]/10 p-3 flex items-center gap-3">
                                        <FileText className="w-5 h-5 text-[#8B6712]" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-bold text-[#002657] truncate">{d.document_type} · {d.doc_number || "—"}</div>
                                            <div className="text-[10px] text-[#002657]/55">{(d.created_at || "").slice(0, 10)}</div>
                                        </div>
                                        <div className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full ${d.status === "approved" ? "bg-emerald-100 text-emerald-700" : d.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                                            {d.status}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
